/**
 * 7-step OIH Heritage Submission Wizard.
 *
 * Steps:
 *   1. Your Information            (PROFILE_SCHEMA)
 *   2. About the Heritage Item     (ABOUT_ITEM_SCHEMA)
 *   3. Important Dates & History   (DATES_SCHEMA — conditional)
 *   4. Provenance & Historical Significance (HISTORICAL_SCHEMA)
 *   5. Upload Files                (custom UI + assistance-request toggle)
 *   6. Privacy, Consent & Agreement (PERMISSION_SCHEMA + consent text)
 *   7. Review & Submit
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../lib/api";
import { FieldRenderer } from "./FieldRenderer";
import {
  PROFILE_SCHEMA, ABOUT_ITEM_SCHEMA, DATES_SCHEMA, HISTORICAL_SCHEMA,
  PERMISSION_SCHEMA, CONSENT_TEXT, UPLOAD_HELP,
  getInitialValues, validateSchema,
} from "../lib/fieldSchemas";
import { Upload, X, FileText, Image as ImageIcon, ChevronLeft, ChevronRight, Save, CheckCircle2, HandHelping } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import logger from "../lib/logger";

const WIZARD_STEPS = [
  { id: "profile",      label: "Your Information" },
  { id: "about",        label: "About the Item" },
  { id: "dates",        label: "Dates & Context" },
  { id: "provenance",   label: "Historical Significance" },
  { id: "artifacts",    label: "Upload Files" },
  { id: "permissions",  label: "Consent" },
  { id: "review",       label: "Review & Submit" },
];

export function SubmissionWizard({ mode = "authenticated", initialData = null, submissionId = null }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedId, setSavedId] = useState(submissionId);

  // Merge item + dates into one payload map since backend accepts a flat item dict.
  const initialItem = initialData?.item || {
    ...getInitialValues(ABOUT_ITEM_SCHEMA),
    ...getInitialValues(DATES_SCHEMA),
  };

  const [profile, setProfile] = useState(() => {
    const base = getInitialValues(PROFILE_SCHEMA);
    if (user) return { ...base, first_name: user.first_name, last_name: user.last_name, email: user.email, ...user.profile };
    return base;
  });
  const [item, setItem] = useState(initialItem);
  const [historical, setHistorical] = useState(() => initialData?.historical || getInitialValues(HISTORICAL_SCHEMA));
  const [artifacts, setArtifacts] = useState(initialData?.artifacts || []);
  const [permissions, setPermissions] = useState(() => initialData?.permissions || getInitialValues(PERMISSION_SCHEMA));
  const [helpRequested, setHelpRequested] = useState(!!initialData?.help_requested);
  const [helpDetails, setHelpDetails] = useState(initialData?.help_details || { contact_method: "Email", best_time: "", notes: "" });
  const [errors, setErrors] = useState({});

  const setKV = (setter) => (k, v) => setter((s) => ({ ...s, [k]: v }));

  const validateStep = () => {
    const id = WIZARD_STEPS[step].id;
    let e = {};
    if (id === "profile")     e = validateSchema(PROFILE_SCHEMA, profile);
    if (id === "about")       e = validateSchema(ABOUT_ITEM_SCHEMA, item);
    if (id === "dates")       e = validateSchema(DATES_SCHEMA, item);
    if (id === "provenance")  e = validateSchema(HISTORICAL_SCHEMA, historical);
    if (id === "permissions") e = validateSchema(PERMISSION_SCHEMA, permissions);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    item, historical, permissions, artifacts,
    help_requested: helpRequested, help_details: helpRequested ? helpDetails : {},
  });

  const saveDraft = async () => {
    if (mode !== "authenticated") return;
    setBusy(true); setError("");
    try {
      if (savedId) {
        await API.put(`/submissions/${savedId}`, buildPayload());
      } else {
        const { data } = await API.post("/submissions", buildPayload());
        setSavedId(data.id);
      }
      toast.success("Draft saved. You can safely close this page and return anytime.");
    } catch (e) {
      logger.error("Draft save failed:", e);
      const msg = formatApiError(e.response?.data?.detail) || "Could not save draft. Please try again.";
      setError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const finalSubmit = async () => {
    if (!validateStep()) return;
    setBusy(true); setError("");
    try {
      if (mode === "public") {
        await API.post("/public/info-submit", {
          contact: profile,
          items: [{ item, historical, permissions, artifacts }],
          help_requested: helpRequested, help_details: helpDetails,
        });
        toast.success("Thank you — your information has been received.");
        nav("/participate/thank-you");
        return;
      }
      let id = savedId;
      if (!id) {
        const { data } = await API.post("/submissions", buildPayload());
        id = data.id;
      } else {
        await API.put(`/submissions/${id}`, buildPayload());
      }
      await API.post(`/submissions/${id}/submit`);
      toast.success("Submission sent to the Heritage team.");
      nav(`/member/submissions/${id}?submitted=1`);
    } catch (e) {
      logger.error("Wizard submit failed:", e);
      const msg = formatApiError(e.response?.data?.detail) || "Submission failed. Please try again.";
      setError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const currentId = WIZARD_STEPS[step].id;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress header */}
      <div className="mb-6">
        <div className="steps-bar" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={WIZARD_STEPS.length}>
          {WIZARD_STEPS.map((s, i) => (
            <div key={s.id} className={`step-dot ${i === step ? "active" : i < step ? "done" : ""}`} title={s.label} />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[13px]">
          <div className="uppercase tracking-widest text-muted-h">
            Step {step + 1} of {WIZARD_STEPS.length}
          </div>
          <div className="text-navy font-medium">{WIZARD_STEPS[step].label}</div>
        </div>
      </div>

      <div className="card-heritage">
        {currentId === "profile"    && <FieldRenderer schema={PROFILE_SCHEMA}    values={profile}    onChange={setKV(setProfile)}    errors={errors} />}
        {currentId === "about"      && <FieldRenderer schema={ABOUT_ITEM_SCHEMA} values={item}       onChange={setKV(setItem)}       errors={errors} />}
        {currentId === "dates"      && <FieldRenderer schema={DATES_SCHEMA}      values={item}       onChange={setKV(setItem)}       errors={errors} />}
        {currentId === "provenance" && <FieldRenderer schema={HISTORICAL_SCHEMA} values={historical} onChange={setKV(setHistorical)} errors={errors} />}
        {currentId === "artifacts"  && (
          <ArtifactsStep
            artifacts={artifacts}
            setArtifacts={setArtifacts}
            authenticated={mode === "authenticated"}
            helpRequested={helpRequested}
            setHelpRequested={setHelpRequested}
            helpDetails={helpDetails}
            setHelpDetails={setHelpDetails}
          />
        )}
        {currentId === "permissions" && (
          <>
            <div className="mb-6">
              <h3 className="font-display text-3xl text-navy mb-2">Consent & Agreement</h3>
              <div className="bg-parchment rounded-lg p-5 space-y-3 text-[14.5px] text-ink-soft leading-relaxed">
                {CONSENT_TEXT.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <FieldRenderer schema={PERMISSION_SCHEMA} values={permissions} onChange={setKV(setPermissions)} errors={errors} />
            <div className="mt-4 text-xs text-muted-h">
              Submission date will be recorded automatically once you press Submit.
            </div>
          </>
        )}
        {currentId === "review" && (
          <ReviewStep
            profile={profile}
            item={item}
            historical={historical}
            artifacts={artifacts}
            permissions={permissions}
            helpRequested={helpRequested}
          />
        )}
        {error && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" role="alert" data-testid="wizard-error">
            {error}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          disabled={step === 0}
          onClick={() => { setError(""); setStep((s) => s - 1); }}
          className="btn-ghost disabled:opacity-40"
          data-testid="wizard-back"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <div className="flex flex-wrap gap-2">
          {mode === "authenticated" && (
            <button onClick={saveDraft} disabled={busy} className="btn-outline" data-testid="wizard-save-draft">
              <Save size={16} /> {busy ? "Saving…" : "Save Draft"}
            </button>
          )}
          {step < WIZARD_STEPS.length - 1 ? (
            <button
              onClick={() => { if (validateStep()) { setError(""); setStep((s) => s + 1); } }}
              className="btn-primary"
              data-testid="wizard-next"
            >
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button onClick={finalSubmit} disabled={busy} className="btn-primary" data-testid="wizard-submit">
              {busy ? "Submitting…" : "Submit for Review"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================
   Step 5 — Upload Files (+ Assistance Request toggle)
   ========================================================== */
function ArtifactsStep({ artifacts, setArtifacts, authenticated, helpRequested, setHelpRequested, helpDetails, setHelpDetails }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const addBlankRow = () => setArtifacts([
    ...artifacts,
    { file_id: null, file_name: "", file_description: "", approximate_date: "", creator: "", notes: "", content_type: "", size: 0 },
  ]);
  const removeRow = (i) => setArtifacts(artifacts.filter((_, idx) => idx !== i));
  const updateRow = (i, k, v) => setArtifacts(artifacts.map((a, idx) => idx === i ? { ...a, [k]: v } : a));

  const uploadFile = async (i, file) => {
    if (!authenticated) {
      updateRow(i, "file_name", file.name);
      return;
    }
    setUploading(true); setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArtifacts((prev) => prev.map((a, idx) => idx === i ? {
        ...a, file_id: data.id, file_name: data.filename,
        content_type: data.content_type, size: data.size,
      } : a));
      toast.success(`Uploaded ${data.filename}`);
    } catch (e) {
      logger.error("Upload failed:", e);
      const msg = formatApiError(e.response?.data?.detail) || "Upload failed";
      setUploadError(msg); toast.error(msg);
    } finally { setUploading(false); }
  };

  const uploadNewFile = async (file) => {
    // Immediately add a row + start upload
    const idx = artifacts.length;
    setArtifacts((prev) => [...prev, { file_id: null, file_name: file.name, file_description: "", approximate_date: "", creator: "", notes: "", content_type: file.type, size: file.size }]);
    await uploadFile(idx, file);
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    for (const f of files.slice(0, 10 - artifacts.length)) {
      await uploadNewFile(f);
    }
  };

  return (
    <div>
      <h3 className="font-display text-3xl text-navy mb-2">{UPLOAD_HELP.title}</h3>
      <p className="text-ink-soft text-[15px] mb-5">{UPLOAD_HELP.description}</p>

      {!authenticated && (
        <div className="mb-4 bg-parchment p-3 rounded-lg text-sm text-ink-soft">
          File uploads become available after creating an account. For now, you can describe the artifact below and a Heritage team member will follow up.
        </div>
      )}
      {uploadError && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" role="alert">{uploadError}</div>}

      {authenticated && artifacts.length < 10 && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`dropzone block mb-6 ${dragOver ? "active" : ""}`}
          data-testid="dropzone"
        >
          <Upload size={28} className="mx-auto mb-2 text-navy" />
          <div className="font-display text-lg text-navy">Drag & drop files here</div>
          <div className="text-sm text-muted-h mt-1">or <span className="text-navy underline">browse from your device</span></div>
          <div className="text-xs text-muted-h mt-2">JPG · PNG · WEBP · PDF · DOC · TXT — up to 20 MB each · max 10 files</div>
          <input
            type="file"
            multiple
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              for (const f of files.slice(0, 10 - artifacts.length)) await uploadNewFile(f);
              e.target.value = "";
            }}
            data-testid="dropzone-input"
          />
        </label>
      )}

      {artifacts.length > 0 && (
        <div className="space-y-4">
          {artifacts.map((a, i) => (
            <div key={a.file_id || `art-${i}`} className="border border-heritage rounded-lg p-4 relative" data-testid={`artifact-row-${i}`}>
              <button type="button" onClick={() => removeRow(i)} className="absolute top-2 right-2 text-muted-h hover:text-red-700 p-1" aria-label="Remove file"><X size={16} /></button>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 flex items-center gap-3">
                  {a.file_id ? (
                    <span className="inline-flex items-center gap-2 text-sm text-ink-soft"><FileText size={16} /> {a.file_name} <span className="text-green-700 text-xs">✓ uploaded</span></span>
                  ) : a.file_name && !authenticated ? (
                    <span className="text-sm text-ink-soft flex items-center gap-2"><ImageIcon size={16} /> {a.file_name} <span className="text-muted-h text-xs">(described only)</span></span>
                  ) : (
                    <label className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-heritage rounded cursor-pointer hover:bg-parchment text-sm text-ink-soft">
                      <Upload size={16} />
                      <span>{uploading ? "Uploading…" : "Choose file"}</span>
                      <input type="file" className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadFile(i, e.target.files[0])}
                        accept="image/*,.pdf,.doc,.docx,.txt" />
                    </label>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="field-label">File Description</label>
                  <input className="input-heritage" value={a.file_description} onChange={(e) => updateRow(i, "file_description", e.target.value)} data-testid={`artifact-desc-${i}`} />
                </div>
                <div>
                  <label className="field-label">Approximate Date</label>
                  <input className="input-heritage" value={a.approximate_date} onChange={(e) => updateRow(i, "approximate_date", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Creator</label>
                  <input className="input-heritage" value={a.creator} onChange={(e) => updateRow(i, "creator", e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="field-label">Notes</label>
                  <textarea rows={2} className="input-heritage" value={a.notes} onChange={(e) => updateRow(i, "notes", e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {authenticated && artifacts.length < 10 && artifacts.length > 0 && (
        <button type="button" onClick={addBlankRow} className="mt-4 btn-outline text-sm" data-testid="artifact-add">
          + Add another file
        </button>
      )}

      {/* Assistance Request toggle */}
      <div className="mt-8 border-t border-heritage pt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-[var(--oih-navy)]"
            checked={helpRequested}
            onChange={(e) => setHelpRequested(e.target.checked)}
            data-testid="help-checkbox"
          />
          <div>
            <div className="text-ink font-medium flex items-center gap-2">
              <HandHelping size={16} className="text-gold" />
              {UPLOAD_HELP.cannot_upload_label}
            </div>
            <div className="text-sm text-muted-h">A member of the Heritage team will contact you to assist.</div>
          </div>
        </label>
        {helpRequested && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Preferred Contact Method</label>
              <select
                className="input-heritage"
                value={helpDetails.contact_method}
                onChange={(e) => setHelpDetails({ ...helpDetails, contact_method: e.target.value })}
                data-testid="help-contact-method"
              >
                <option>Email</option><option>Phone</option><option>Text Message</option>
              </select>
            </div>
            <div>
              <label className="field-label">Best Time to Contact</label>
              <input
                className="input-heritage"
                value={helpDetails.best_time}
                onChange={(e) => setHelpDetails({ ...helpDetails, best_time: e.target.value })}
                data-testid="help-best-time"
              />
            </div>
            <div className="col-span-2">
              <label className="field-label">Notes</label>
              <textarea
                className="input-heritage" rows={3}
                value={helpDetails.notes}
                onChange={(e) => setHelpDetails({ ...helpDetails, notes: e.target.value })}
                data-testid="help-notes"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================
   Step 7 — Review & Submit
   ========================================================== */
const REVIEW_BLOCKS = [
  { title: "About You",         schemas: [PROFILE_SCHEMA],     source: "profile" },
  { title: "About the Item",    schemas: [ABOUT_ITEM_SCHEMA],  source: "item" },
  { title: "Dates & Context",   schemas: [DATES_SCHEMA],       source: "item" },
  { title: "Historical Context", schemas: [HISTORICAL_SCHEMA], source: "historical" },
  { title: "Consent",           schemas: [PERMISSION_SCHEMA],  source: "permissions" },
];

function ReviewStep({ profile, item, historical, artifacts, permissions, helpRequested }) {
  const data = { profile, item, historical, permissions };
  return (
    <div>
      <h3 className="font-display text-3xl text-navy mb-2">Review & Submit</h3>
      <p className="text-ink-soft mb-6">Please review your submission before sending it to the Heritage team.</p>

      {REVIEW_BLOCKS.map((block) => (
        <ReviewBlock key={block.title} title={block.title} schemas={block.schemas} data={data[block.source]} allValues={item} />
      ))}

      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Files</div>
        {artifacts.length === 0 ? (
          <div className="text-sm text-ink-soft italic">No files attached.</div>
        ) : (
          <ul className="space-y-1 text-sm text-ink-soft">
            {artifacts.map((a, i) => (
              <li key={a.file_id || i}>• {a.file_name || "(unnamed)"}{a.file_description ? ` — ${a.file_description}` : ""}</li>
            ))}
          </ul>
        )}
      </div>

      {helpRequested && (
        <div className="mb-6 bg-[var(--oih-parchment)] p-3 rounded-lg text-sm text-ink-soft">
          ✓ You've requested help from the Heritage team.
        </div>
      )}

      <div className="mt-6 bg-paper border border-heritage rounded-lg p-4 text-sm text-ink-soft flex gap-3">
        <CheckCircle2 size={20} className="text-navy flex-shrink-0 mt-0.5" />
        <div>By submitting, you confirm the information you have provided is accurate and understand
          that submission does not guarantee publication.</div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, schemas, data, allValues }) {
  const fields = schemas.flatMap((s) => s.sections.flatMap((sec) => sec.fields));
  return (
    <div className="mb-6">
      <div className="text-xs uppercase tracking-widest text-muted-h mb-2">{title}</div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {fields.map((f) => {
          if (f.showIf && !f.showIf(allValues)) return null;
          const v = data[f.key];
          if (v === "" || v == null || v === false) return null;
          const isLong = String(v).length > 60 || f.type === "textarea";
          return (
            <div key={f.key} className={isLong ? "col-span-2" : ""}>
              <dt className="text-muted-h">{f.label}</dt>
              <dd className="text-ink whitespace-pre-wrap">{f.type === "checkbox" ? "Yes" : String(v)}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
